import { ref, computed } from 'vue'

/**
 * Filter Manager to handle multiple filters of different types
 */
export class FilterManager {
	constructor() {
		this.filters = ref(new Map())
		this.listeners = []
	}

	/**
	 * Add a filter to the manager
	 * @param {BaseFilter} filter 
	 */
	addFilter(filter) {
		this.filters.value.set(filter.id, filter)
		this._notifyChange()
	}

	/**
	 * Remove a filter by ID
	 * @param {string} filterId 
	 */
	removeFilter(filterId) {
		const removed = this.filters.value.delete(filterId)
		if (removed) {
			this._notifyChange()
		}
		return removed
	}

	/**
	 * Get a filter by ID
	 * @param {string} filterId 
	 * @returns {BaseFilter|undefined}
	 */
	getFilter(filterId) {
		return this.filters.value.get(filterId)
	}

	/**
	 * Get all filters of a specific type
	 * @param {string} type 
	 * @returns {BaseFilter[]}
	 */
	getFiltersByType(type) {
		return Array.from(this.filters.value.values()).filter(filter => filter.type === type)
	}

	/**
	 * Get all active filters
	 * @returns {BaseFilter[]}
	 */
	getActiveFilters() {
		return Array.from(this.filters.value.values()).filter(filter => filter.active)
	}

	/**
	 * Toggle a filter by ID
	 * @param {string} filterId 
	 * @returns {boolean} - New active state
	 */
	toggleFilter(filterId) {
		const filter = this.getFilter(filterId)
		if (filter) {
			const newState = filter.toggle()
			this._notifyChange()
			return newState
		}
		return false
	}

	/**
	 * Set filter active state
	 * @param {string} filterId 
	 * @param {boolean} active 
	 */
	setFilterActive(filterId, active) {
		const filter = this.getFilter(filterId)
		if (filter) {
			filter.setActive(active)
			this._notifyChange()
		}
	}

	/**
	 * Apply all active filters of a given type to an array of items
	 * @param {any[]} items 
	 * @param {string} filterType 
	 * @param {Object} context 
	 * @returns {any[]}
	 */
	applyFilters(items, filterType, context = {}) {
		const activeFilters = this.getActiveFilters().filter(filter => filter.type === filterType)
		
		if (activeFilters.length === 0) {
			return items
		}

		return items.filter(item => {
			return activeFilters.every(filter => filter.apply(item, context))
		})
	}

	/**
	 * Get room IDs that have bookings matching active booking filters
	 * @param {any[]} bookings - Array of booking events
	 * @returns {Set<string>} - Set of room IDs with matching bookings
	 */
	getRoomIdsWithFilteredBookings(bookings) {
		const activeBookingFilters = this.getActiveFilters().filter(filter => filter.type === 'booking')
		
		if (activeBookingFilters.length === 0) {
			return null // No booking filters active
		}

		const filteredBookings = this.applyFilters(bookings, 'booking')
		return new Set(filteredBookings.map(booking => booking.resourceId).filter(Boolean))
	}

	/**
	 * Clear all filters
	 */
	clearAllFilters() {
		this.filters.value.forEach(filter => {
			// Use clearSelection() if available (properly clears internal state)
			// Otherwise fall back to setActive(false)
			if (typeof filter.clearSelection === 'function') {
				filter.clearSelection()
			} else {
				filter.setActive(false)
			}
		})
		this._notifyChange()
	}

	/**
	 * Get filter configurations for UI rendering
	 * @param {string} type - Optional filter type
	 * @returns {Object[]}
	 */
	getFilterConfigs(type = null) {
		let filters = Array.from(this.filters.value.values())
		if (type) {
			filters = filters.filter(filter => filter.type === type)
		}
		return filters.map(filter => filter.getConfig())
	}

	/**
	 * Serialize all filter states
	 * @returns {Object}
	 */
	serialize() {
		const state = {}
		this.filters.value.forEach((filter, id) => {
			state[id] = filter.serialize()
		})
		return state
	}

	/**
	 * Restore filter states from serialized data
	 * @param {Object} state 
	 */
	deserialize(state) {
		Object.entries(state).forEach(([id, filterState]) => {
			const filter = this.getFilter(id)
			if (filter) {
				filter.deserialize(filterState)
			}
		})
		this._notifyChange()
	}

	/**
	 * Add change listener
	 * @param {Function} callback 
	 */
	onChange(callback) {
		this.listeners.push(callback)
		return () => {
			const index = this.listeners.indexOf(callback)
			if (index > -1) {
				this.listeners.splice(index, 1)
			}
		}
	}

	/**
	 * Notify all listeners of changes
	 * @private
	 */
	_notifyChange() {
		this.listeners.forEach(callback => callback(this))
	}

	/**
	 * Computed property for reactive UI updates
	 */
	get reactiveFilters() {
		return computed(() => Array.from(this.filters.value.values()))
	}

	/**
	 * Computed property for active filter count
	 */
	get activeFilterCount() {
		return computed(() => this.getActiveFilters().length)
	}
}

/**
 * Create a global filter manager instance
 */
export function createFilterManager() {
	return new FilterManager()
}